/**
 * Core data hooks for the admin dashboard.
 *
 * Extracts all fetch logic from AdminDashboard.tsx into reusable hooks.
 * Each hook manages its own loading/error state and provides refresh functions.
 */
import { useState, useRef, useCallback } from 'react'
import type { Session } from '@supabase/supabase-js'
import type { Booking, SortConfig } from '../lib/types'
import { getBookings, getStats } from '../lib/admin-api'

const PAGE_SIZE = 20

// --- Price Drops ---

/**
 * Stable signature for change detection on the 60s auto-refresh. Two
 * fetches yield the same signature iff nothing the UI visibly depends on
 * (id set, ordering, savings amounts, cheaper flag, last-checked time,
 * Linear issue state) has changed. When identical, we skip setPriceDrops
 * to avoid triggering the downstream sort/memo cascade and re-rendering
 * every row every minute for nothing.
 */
function priceDropsSignature(bookings: Booking[]): string {
  return bookings
    .map(b =>
      [
        b.id,
        b.potential_savings ?? 0,
        b.latest_price ?? 0,
        b.has_cheaper_option ? 1 : 0,
        b.last_checked_at ?? '',
        b.linear_issue?.status ?? '',
      ].join(':'),
    )
    .join('|')
}

export function usePriceDrops(session: Session) {
  const [priceDrops, setPriceDrops] = useState<Booking[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const signatureRef = useRef('')

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getBookings(session, { price_drops: true, limit: 100 })
      const next = (data.bookings || []) as Booking[]
      const nextSignature = priceDropsSignature(next)
      if (nextSignature !== signatureRef.current) {
        signatureRef.current = nextSignature
        setPriceDrops(next)
      }
      setTotal(data.pagination?.total || 0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [session])

  const remove = useCallback((bookingId: string) => {
    setPriceDrops(prev => {
      const next = prev.filter(b => b.id !== bookingId)
      signatureRef.current = priceDropsSignature(next)
      return next
    })
    setTotal(prev => Math.max(0, prev - 1))
  }, [])

  return { priceDrops, total, loading, error, fetch, remove }
}

// --- Active Bookings ---

export function useActiveBookings(session: Session) {
  const [bookings, setBookings] = useState<Booking[]>([])
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getBookings(session, { active_only: true, limit: PAGE_SIZE, offset: 0 })
      setBookings((data.bookings || []) as Booking[])
      setOffset(PAGE_SIZE)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [session])

  const loadMore = useCallback(async () => {
    setLoadingMore(true)
    try {
      const data = await getBookings(session, { active_only: true, limit: PAGE_SIZE, offset })
      setBookings(prev => [...prev, ...((data.bookings || []) as Booking[])])
      setOffset(prev => prev + PAGE_SIZE)
    } catch (err) {
      console.error('Failed to load more bookings:', err)
    } finally {
      setLoadingMore(false)
    }
  }, [session, offset])

  const remove = useCallback((bookingId: string) => {
    setBookings(prev => prev.filter(b => b.id !== bookingId))
  }, [])

  return { bookings, loading, loadingMore, error, fetch, loadMore, remove }
}

// --- Collapsible bookings (inactive / archived) ---

/**
 * Shared hook for the "Inactive" and "Archived" collapsible sections on
 * the dashboard. Both fetch paginated bookings behind a toggle and expose
 * the same surface; only the filter flag differs.
 */
export function useCollapsibleBookings(session: Session, mode: 'inactive' | 'archived') {
  const [bookings, setBookings] = useState<Booking[]>([])
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const filterFlag = mode === 'inactive' ? { inactive_only: true } : { archived_only: true }

  const fetch = useCallback(async (loadMore = false) => {
    setLoading(true)
    try {
      const currentOffset = loadMore ? offset : 0
      const data = await getBookings(session, { ...filterFlag, limit: PAGE_SIZE, offset: currentOffset })
      const newBookings = (data.bookings || []) as Booking[]
      setBookings(prev => (loadMore ? [...prev, ...newBookings] : newBookings))
      setOffset(currentOffset + PAGE_SIZE)
    } catch (err) {
      console.error(`Failed to fetch ${mode} bookings:`, err)
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, offset, mode])

  const toggle = useCallback(() => {
    const willExpand = !expanded
    setExpanded(willExpand)
    if (willExpand && bookings.length === 0) {
      fetch()
    }
  }, [expanded, bookings.length, fetch])

  const remove = useCallback((bookingId: string) => {
    setBookings(prev => prev.filter(b => b.id !== bookingId))
  }, [])

  const refetch = useCallback(() => {
    if (expanded) {
      fetch()
    } else {
      setBookings([])
    }
  }, [expanded, fetch])

  return { bookings, loading, expanded, toggle, loadMore: () => fetch(true), remove, refetch }
}


// --- Search ---

export function useSearch(session: Session) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Booking[] | null>(null)
  const [searching, setSearching] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const search = useCallback((newQuery: string) => {
    setQuery(newQuery)

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    if (!newQuery.trim()) {
      setResults(null)
      setSearching(false)
      return
    }

    timeoutRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const data = await getBookings(session, { search: newQuery, limit: 50 })
        setResults((data.bookings || []) as Booking[])
      } catch (err) {
        console.error('Search error:', err)
      } finally {
        setSearching(false)
      }
    }, 300)
  }, [session])

  return { query, results, searching, search, setResults }
}

// --- Stats ---

export function useAdminStats(session: Session) {
  const [stats, setStats] = useState<{
    total_bookings: number
    active_bookings: number
    archived_bookings: number
    revenue_earned?: number
    savings_delivered?: number
    success_rate?: number
  } | null>(null)

  const fetch = useCallback(async () => {
    try {
      const data = await getStats(session)
      if (data.success) {
        setStats(data.stats)
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err)
    }
  }, [session])

  return { stats, fetch }
}

// --- Sort state ---

export function useSortConfig() {
  const [config, setConfig] = useState<SortConfig>({ column: '', direction: null })

  const handleSort = useCallback((column: string) => {
    setConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }, [])

  return { config, handleSort }
}
